import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { loginRequest, msalConfig } from '~/services/auth-service';
import { isMsalConfigured, getConfigurationError } from '~/services/auth-service';
import '~/styles/Signin.css';

// Constants
const BRAND_NAME = 'PAC';
const WELCOME_TITLE = 'Welcome!';
const WELCOME_SUBTITLE = 'Sign in with your corporate account to continue';
const BRAND_TITLE = 'Patient Auto Calling';
const BRAND_SUBTITLE = 'Powered by Bayshore HealthCare';

// Components
function ErrorIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="2" />
      <path d="M10 6v5M10 13v1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function MicrosoftLogo() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect x="1" y="1" width="10" height="10" fill="#f25022" />
      <rect x="13" y="1" width="10" height="10" fill="#7fba00" />
      <rect x="1" y="13" width="10" height="10" fill="#00a4ef" />
      <rect x="13" y="13" width="10" height="10" fill="#ffb900" />
    </svg>
  );
}

function LoadingSpinner() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeDasharray="32" strokeDashoffset="32">
        <animate attributeName="stroke-dasharray" dur="2s" values="0 32;16 16;0 32;0 32" repeatCount="indefinite" />
        <animate attributeName="stroke-dashoffset" dur="2s" values="0;-16;-32;-32" repeatCount="indefinite" />
      </circle>
    </svg>
  );
}

function ConfigurationErrorBanner({ message }: { message: string }) {
  return (
    <div className="config-error-banner" role="alert">
      <div>
        <strong>Configuration Required</strong>
        <p className="config-error-text">{message}</p>
      </div>
    </div>
  );
}

function AuthenticationErrorBanner({ message }: { message: string }) {
  return (
    <div className="error-banner" role="alert">
      <ErrorIcon />
      <span>{message}</span>
    </div>
  );
}

function SigninForm({
  onLogin,
  loading,
  disabled,
  configError,
  authError
}: {
  onLogin: () => void;
  loading: boolean;
  disabled: boolean;
  configError: string | null;
  authError: string | null;
}) {
  return (
    <div className="signin-form-container">
      <div className="brand-pill">{BRAND_NAME}</div>
      <h1 className="welcome">{WELCOME_TITLE}</h1>
      <p className="muted">{WELCOME_SUBTITLE}</p>

      <div className="form">
        {configError && <ConfigurationErrorBanner message={configError} />}
        {authError && <AuthenticationErrorBanner message={authError} />}

        <button
          type="button"
          className="ms-btn"
          onClick={onLogin}
          disabled={disabled || loading}
          aria-label="Sign in with Microsoft"
          aria-busy={loading}
        >
          {loading ? (
            <>
              <LoadingSpinner />
              <span>Signing in...</span>
            </>
          ) : (
            <>
              <MicrosoftLogo />
              <span>Sign in with Microsoft</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function SigninBranding() {
  return (
    <div className="signin-branding">
      <div className="brand-logo-large">{BRAND_NAME}</div>
      <h2 className="signin-art-title">{BRAND_TITLE}</h2>
      <p className="signin-art-sub">{BRAND_SUBTITLE}</p>
    </div>
  );
}

export default function Signin() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const configError = getConfigurationError();
  const isConfigured = isMsalConfigured();
  const urlError = searchParams.get('error');
  const authError = error || (urlError ? 'Authentication failed. Please try again.' : null);

  useEffect(() => {
    // Check if user is already authenticated
    if (accounts.length > 0) {
      const redirectTo = searchParams.get('redirectTo') ?? '/dashboard';
      navigate(redirectTo, { replace: true });
    }
  }, [accounts, searchParams, navigate]);

  const handleSSOLogin = () => {
    setError('');
    setLoading(true);

    try {
      instance.loginRedirect({
        scopes: loginRequest.scopes,
        redirectUri: msalConfig.auth.redirectUri,
      });
    } catch (err: unknown) {
      console.error('[Signin] Login error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to sign in';
      setError(errorMessage);
      setLoading(false);
    }
  };

  return (
    <div className="signin-page">
      <div className="signin-left">
        <SigninForm
          onLogin={handleSSOLogin}
          loading={loading}
          disabled={!isConfigured}
          configError={configError}
          authError={authError}
        />
      </div>
      <div className="signin-right">
        <SigninBranding />
      </div>
    </div>
  );
}
